import { ContractFieldGrouping, FilterOption, FilterOptionType } from "../common/components/filterPopover/FilterPopover.types.ts";
import { mapInputFieldTypeToFilterOptionType } from "@common/components/filterPopover/inputFieldFilterTypeMapping.ts";
import { useTranslation } from "../localization/useTranslation.ts";
import { EntityType } from "../common/Common.types.ts";
import {
    getSelectableBusinessAreasUrl,
    getSelectableEntityTemplatesUrl,
    paymentTermsUrl,
    selectableContractTypesUrl,
    selectableGroupEntitiesForUserUrl,
    selectableTagsUrl,
    useContractInputFieldsMapping,
} from "../common/hooks/useSelectableItemQueries.ts";
import { contractManagersUrl, getResponsibleQueryUrl } from "../user/hooks/useResponsibleQuery.ts";
import { useUserContext } from "../auth/userContextProvider/UserContextProvider.tsx";
import { InputFieldTypeEnum } from "../entityTemplates/inputFields/InputFields.types.ts";
import { ContractFilterStatus } from "./Contracts.types.ts";
import { ContractFilterRequest, FILTER_ID_TO_BACKEND_PROPERTY } from "./ContractFilter.types.ts";
import { usePlanContext } from "../auth/planProvider/PlanProvider.tsx";
import { TemplateEntityType } from "../entityTemplates/EntityTemplate.types.ts";
import { hydrateFilterOperators } from "@common/components/filterBar/filterOperatorHydration.ts";
import useCountryHook from "@common/hooks/useCountryList";

export function useContractFilter(filterData?: Partial<ContractFilterRequest>) {
    const { translateString, translateContractStatusEnum } = useTranslation();
    const { permissions } = useUserContext();
    const { platformFeatures } = usePlanContext();
    const { selectableCountries } = useCountryHook();

    // Helper function to extract values from filter structures
    function extractFilterValue(filter: any): any {
        if (!filter) return "";

        if (filter.value !== undefined && filter.filterOperator !== undefined) {
            if (Array.isArray(filter.value)) {
                return filter.value.map((item: string) => item.toString());
            }

            return filter.value;
        }

        // For ranges
        if (filter.from !== undefined || filter.to !== undefined) {
            return filter;
        }

        // For direct values
        return filter;
    }

    const customInputFieldsQuery = useContractInputFieldsMapping();

    let contractFilterOptions = getStandardFilterProperties();

    if (customInputFieldsQuery.data && customInputFieldsQuery.data.customFieldsSelectableValues.length > 0) {
        customInputFieldsQuery.data.customFieldsSelectableValues
            .sort((x, y) => (x.name > y.name ? 1 : -1))
            .forEach((x) => {
                // Check if this custom field has a value in filterData
                const customFieldValue = filterData?.customFieldFilters?.find((cf) => cf.inputFieldId === x.id);

                const option: FilterOption = {
                    id: `custom-field-${x.id}`,
                    label: x.name,
                    type: mapInputFieldTypeToFilterOptionType(x.type),
                    value: customFieldValue?.value ?? "",
                    customInputType: x.type,
                    grouping: ContractFieldGrouping.CustomFields,
                };

                // Set dataUrl for specific types
                switch (x.type) {
                    case InputFieldTypeEnum.UserField:
                        option.dataUrl = getResponsibleQueryUrl();
                        break;
                    case InputFieldTypeEnum.LegalEntityField:
                        option.entityType = EntityType.LegalEntity;
                        break;
                    case InputFieldTypeEnum.CountryField:
                        option.fixedData = selectableCountries;
                        break;
                    case InputFieldTypeEnum.SingleDropDown:
                    case InputFieldTypeEnum.MultiDropDown:
                        option.dataUrl = `/settings/input-fields/${x.id}/select-options`;
                        break;
                }

                contractFilterOptions.push(option);
            });
    }

    hydrateFilterOperators(contractFilterOptions, filterData, FILTER_ID_TO_BACKEND_PROPERTY);

    return { contractFilterOptions, isLoading: customInputFieldsQuery.isLoading };

    function getStandardFilterProperties() {
        let filterOptions: Array<FilterOption> = [
            // Show only mine
            {
                id: "show-only-mine",
                label: translateString("show"),
                type: FilterOptionType.Boolean,
                value: filterData?.showOnlyMine ?? "",
                fixedData: [
                    { id: "0", name: translateString("all") },
                    { id: "1", name: translateString("my") },
                ],
                grouping: ContractFieldGrouping.Contract,
            },
            // Basic filters
            {
                id: "contract-ids",
                label: translateString("contract"),
                type: FilterOptionType.SelectField,
                value: filterData ? extractFilterValue(filterData.id) : "",
                entityType: EntityType.Contract,
                grouping: ContractFieldGrouping.Contract,
            },
            {
                id: "template-ids",
                label: translateString("contractTemplate"),
                type: FilterOptionType.SelectField,
                value: filterData ? extractFilterValue(filterData.templateIds) : "",
                dataUrl: getSelectableEntityTemplatesUrl(TemplateEntityType.Contract, true),
                grouping: ContractFieldGrouping.Contract,
            },
            {
                id: "statuses",
                label: translateString("status"),
                type: FilterOptionType.SelectField,
                value: filterData ? extractFilterValue(filterData.status) : "",
                fixedData: [
                    { id: ContractFilterStatus.Draft.toString(), name: translateContractStatusEnum(ContractFilterStatus.Draft.toString()) },
                    { id: ContractFilterStatus.Approved.toString(), name: translateContractStatusEnum(ContractFilterStatus.Approved.toString()) },
                    { id: ContractFilterStatus.Archived.toString(), name: translateContractStatusEnum(ContractFilterStatus.Archived.toString()) },
                    { id: ContractFilterStatus.Expired.toString(), name: translateString("expired") },
                ],
                grouping: ContractFieldGrouping.Contract,
            },
            {
                id: "description",
                label: translateString("description"),
                type: FilterOptionType.TextField,
                value: filterData ? extractFilterValue(filterData.description) : "",
                grouping: ContractFieldGrouping.Contract,
            },
            {
                id: "reference-number",
                label: translateString("referenceNumber"),
                type: FilterOptionType.TextField,
                value: filterData ? extractFilterValue(filterData.referenceNumber) : "",
                grouping: ContractFieldGrouping.Contract,
            },
            {
                id: "contract-type-ids",
                label: translateString("contractType"),
                type: FilterOptionType.SelectField,
                value: filterData ? extractFilterValue(filterData.contractTypeIds) : "",
                dataUrl: selectableContractTypesUrl,
                grouping: ContractFieldGrouping.Contract,
            },
            {
                id: "business-area-ids",
                label: translateString("businessArea"),
                type: FilterOptionType.SelectField,
                value: filterData ? extractFilterValue(filterData.businessAreaIds) : "",
                dataUrl: getSelectableBusinessAreasUrl(false),
                grouping: ContractFieldGrouping.Contract,
            },
            {
                id: "has-business-area",
                label: translateString("hasBusinessArea"),
                type: FilterOptionType.Boolean,
                value: filterData?.hasBusinessArea ?? "",
                grouping: ContractFieldGrouping.Contract,
            },
            {
                id: "note",
                label: translateString("note"),
                type: FilterOptionType.TextField,
                value: filterData ? extractFilterValue(filterData.note) : "",
                grouping: ContractFieldGrouping.Contract,
            },
            {
                id: "contract-manager-ids",
                label: translateString("contractManager"),
                type: FilterOptionType.SelectField,
                value: filterData ? extractFilterValue(filterData.contractManagerIds) : "",
                dataUrl: contractManagersUrl,
                grouping: ContractFieldGrouping.Access,
            },
            {
                id: "additional-user-ids",
                label: translateString("additionalUserAccess"),
                type: FilterOptionType.SelectField,
                value: filterData ? extractFilterValue(filterData.additionalUserIds) : "",
                dataUrl: getResponsibleQueryUrl(),
                grouping: ContractFieldGrouping.Access,
            },
            {
                id: "access-business-area-ids",
                label: translateString("businessAreaAccess"),
                type: FilterOptionType.SelectField,
                value: filterData ? extractFilterValue(filterData.accessBusinessAreaIds) : "",
                dataUrl: getSelectableBusinessAreasUrl(false),
                grouping: ContractFieldGrouping.Access,
            },
            {
                id: "excluded-user-ids",
                label: translateString("excludedUsers"),
                type: FilterOptionType.SelectField,
                value: filterData ? extractFilterValue(filterData.excludedUserIds) : "",
                dataUrl: getResponsibleQueryUrl(),
                grouping: ContractFieldGrouping.Access,
            },
            {
                id: "visible-to-all",
                label: translateString("visibleToAllUsers"),
                type: FilterOptionType.Boolean,
                value: filterData?.visibleToAllUsers ?? "",
                grouping: ContractFieldGrouping.Access,
            },
            {
                id: "legal-entity-ids",
                label: translateString("groupEntityAccess"),
                type: FilterOptionType.SelectField,
                value: filterData ? extractFilterValue(filterData.legalEntityIds) : "",
                dataUrl: selectableGroupEntitiesForUserUrl,
                grouping: ContractFieldGrouping.Access,
            },
            {
                id: "start-date-range",
                label: translateString("startDate"),
                type: FilterOptionType.DateField,
                value: filterData?.startDateRange || "",
                grouping: ContractFieldGrouping.Period,
            },
            {
                id: "end-date-range",
                label: translateString("endDate"),
                type: FilterOptionType.DateField,
                value: filterData?.endDateRange || "",
                grouping: ContractFieldGrouping.Period,
            },
            {
                id: "renewal-date-range",
                label: translateString("renewalDate"),
                type: FilterOptionType.DateField,
                value: filterData?.renewalDateRange || "",
                grouping: ContractFieldGrouping.Period,
                fixedData: [
                    { id: "0", name: translateString("within14Days") },
                    { id: "1", name: translateString("inAMonth") },
                    { id: "2", name: translateString("in2Months") },
                    { id: "3", name: translateString("in3Months") },
                ],
            },
            {
                id: "signature-date-range",
                label: translateString("signatureDate"),
                type: FilterOptionType.DateField,
                value: filterData?.signatureDateRange || "",
                grouping: ContractFieldGrouping.Period,
            },
            {
                id: "expiry-notification-days",
                label: translateString("notificationOfContractExpiry"),
                type: FilterOptionType.Number,
                value: filterData ? extractFilterValue(filterData.expiryNotificationDays) : "",
                grouping: ContractFieldGrouping.Period,
            },
            {
                id: "within-expiry-notification-period",
                label: translateString("withinExpiryNotificationPeriod"),
                type: FilterOptionType.Boolean,
                value: filterData?.withinExpiryNotificationPeriod ?? "",
                grouping: ContractFieldGrouping.Period,
            },
            {
                id: "total-amount-range",
                label: translateString("totalAmount"),
                type: FilterOptionType.Number,
                value: filterData?.totalAmount || "",
                grouping: ContractFieldGrouping.Terms,
            },
            {
                id: "finalized-amount-range",
                label: translateString("finalisedAmount"),
                type: FilterOptionType.Number,
                value: filterData?.finalizedAmount || "",
                grouping: ContractFieldGrouping.Terms,
            },
            {
                id: "has-finalized-amount",
                label: translateString("hasFinalisedAmount"),
                type: FilterOptionType.Boolean,
                value: filterData?.hasFinalizedAmount ?? "",
                grouping: ContractFieldGrouping.Terms,
            },
            {
                id: "currency-codes",
                label: translateString("currency"),
                type: FilterOptionType.Currency,
                value: filterData ? extractFilterValue(filterData.currencyCodes) : "",
                grouping: ContractFieldGrouping.Terms,
            },
            {
                id: "payment-term-ids",
                label: translateString("paymentTerm"),
                type: FilterOptionType.SelectField,
                value: filterData ? extractFilterValue(filterData.paymentTermIds) : "",
                dataUrl: paymentTermsUrl,
                grouping: ContractFieldGrouping.Terms,
            },
            {
                id: "special-terms",
                label: translateString("specialTerms"),
                type: FilterOptionType.TextField,
                value: filterData ? extractFilterValue(filterData.specialTerms) : "",
                grouping: ContractFieldGrouping.Terms,
            },
            {
                id: "external-contractParty-ids",
                label: translateString("externalContractParties"),
                type: FilterOptionType.SelectField,
                value: filterData ? extractFilterValue(filterData.externalContractPartyIds) : "",
                dataUrl: "/LegalEntity/selectable-external-entities",
                grouping: ContractFieldGrouping.ContractParties,
            },
            {
                id: "has-external-contract-party",
                label: translateString("hasExternalContractParty"),
                type: FilterOptionType.Boolean,
                value: filterData?.hasExternalContractParty ?? "",
                grouping: ContractFieldGrouping.ContractParties,
            },
            {
                id: "internal-contractParty-ids",
                label: translateString("internalContractParties"),
                type: FilterOptionType.SelectField,
                value: filterData ? extractFilterValue(filterData.internalContractPartyIds) : "",
                dataUrl: "/LegalEntity/selectableGroupEntities",
                grouping: ContractFieldGrouping.ContractParties,
            },
            {
                id: "created-date-range",
                label: translateString("createdDate"),
                type: FilterOptionType.DateField,
                value: filterData?.createdDateRange || "",
                grouping: ContractFieldGrouping.Contract,
            },
            {
                id: "modified-date-range",
                label: translateString("lastModified"),
                type: FilterOptionType.DateField,
                value: filterData?.modifiedDateRange || "",
                grouping: ContractFieldGrouping.Contract,
            },
        ];

        if (permissions.canAccessTags && platformFeatures.tags) {
            filterOptions.push({
                id: "tag-ids",
                label: translateString("tags"),
                type: FilterOptionType.SelectField,
                value: filterData ? extractFilterValue(filterData.tagIds) : "",
                dataUrl: selectableTagsUrl,
                grouping: ContractFieldGrouping.Contract,
            });
        }

        if (!permissions.contractManagementPermissions.canManageExcludedUsers) {
            filterOptions = filterOptions.filter((x) => x.id !== "excluded-user-ids");
        }

        return filterOptions;
    }
}
